<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.payments', 2),
                        href: meta.links.payments_group_select,
                    },
                    {
                        name: student.group.name,
                        href: meta.links.payments_group_show,
                    },
                    {
                        name: student.full_name,
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-6">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-4">
                    <div class="">
                        <FormLabel :value="$t('messages.type')" for="select_id" />
                        <FormSelect
                            id="select_id"
                            v-model="form.select_id"
                            :options="options.selects"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.select_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="w-full flex space-x-4 justify-end mt-3" v-if="meta.can.create">
            <StudentPaymentCreateFormPanel
                :student="student"
                :url="meta.links.store"
                :selects="options.selects"
                :scholarships="options.scholarships"
            />
            <StudentPaymentTransferFormPanel
                :student="student"
                :url="meta.links.transfer_store"
                :selects="options.selects"
                :scholarships="options.scholarships"
                :recipients="options.possibleRecipients"
            />
        </div>

        <AppWell v-if="paymentsSummary.length" class="mt-3">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <CardPanel v-for="select in paymentsSummary" :key="select.id">
                    <div class="flex justify-between">
                        <div>{{ select.name }}</div>
                        <div>{{ select.total }} / {{ select.quota }}</div>
                    </div>

                    <ProgressBar :percent="select.percent" :color="select.percent >= 100 ? 'green' : 'gray'" />
                </CardPanel>
            </div>
        </AppWell>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.quantity') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.type') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.is_scholarship') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.comment') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(studentPayment, index) in studentPayments.data"
                                :key="studentPayment.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ studentPayment.paid_at_display }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ studentPayment.amount }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ studentPayment.select_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ studentPayment.scholarship }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ studentPayment.comment }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <StudentPaymentEditFormPanel
                                        v-if="studentPayment.can.edit"
                                        :student="student"
                                        :url="studentPayment.links.update"
                                        :selects="options.selects"
                                        square-button
                                        :payment="studentPayment"
                                        :scholarships="options.scholarships"
                                    />
                                    <InertiaSquareConfirmActionButton
                                        :confirm-text="$t('messages.delete')"
                                        :href="studentPayment.links.delete"
                                        danger
                                        method="delete"
                                        :title="$t('messages.are_you_sure?')"
                                        v-if="studentPayment.can.delete"
                                    >
                                        <template v-slot:icon>
                                            <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                        </template>
                                    </InertiaSquareConfirmActionButton>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="studentPayments" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import StudentPaymentCreateFormPanel from '@/components/StudentPaymentCreateFormPanel.vue';
import StudentPaymentEditFormPanel from '@/components/StudentPaymentEditFormPanel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import AppWell from '@/Shared/AppWell.vue';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';
import CardPanel from '@/Shared/CardPanel.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import StudentPaymentTransferFormPanel from '@/components/StudentPaymentTransferFormPanel.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        InertiaPagination,
        FormSelect,
        FormLabel,
        FormError,
        StudentPaymentCreateFormPanel,
        StudentPaymentEditFormPanel,
        InertiaSquareConfirmActionButton,
        StudentPaymentTransferFormPanel,
        AppWell,
        ProgressBar,
        CardPanel,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'studentPayments', 'options', 'filters', 'student', 'paymentsSummary'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
                select_id: this.filters.select_id,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
